import Cancel from "@mui/icons-material/Cancel";
import CheckCircle from "@mui/icons-material/CheckCircle";
import PersonAdd from "@mui/icons-material/PersonAdd";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import RemoveFilled from "@mui/icons-material/Remove";
import RestartAlt from "@mui/icons-material/RestartAlt";
import { Box, Alert, Tooltip, Typography } from "@mui/material";
import {
  GridActionsCellItem,
  type GridActionsCellItemProps,
  GridColDef,
} from "@mui/x-data-grid-premium";
import { ConfirmDialog } from "components/ConfirmDialog";
import { IssueCount } from "components/IssueCount";
import { useReviewerIssues, useReviewersIssues } from "contexts/SubmissionFormContext";
import { ReviewMode, ReviewerStatus, Permission } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { SubmissionFormData } from "hooks/useSubmissionForm";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { UseFormSetValue } from "react-hook-form";
import { prettyPrintDate } from "util/dates";

import { DataGridWithAddButton } from "./DataGridWithAddButton";
import { renderIssueMessage } from "./helpers/renderIssueMessage";
import { PriorityOrderSwitch } from "./PriorityOrderSwitch";
import { Reviewer } from "./ReviewersEditList";
import { SubmissionFormDatagrid } from "./SubmissionFormDatagrid";

type ReviewersDatagridProps = {
  reviewers: Reviewer[];
  setReviewers?: (reviewers: Reviewer[]) => void;
  reviewMode: ReviewMode;
  setValue?: UseFormSetValue<SubmissionFormData>;
  handleAddReviewer: () => void;
  submissionDueDate?: DateTime;
  disabled?: boolean;
  isStaff?: boolean;
};

function setReviewerOrder(reviewers: Reviewer[]) {
  const hasStaff = reviewers.some((r) => r.isStaff);
  reviewers.forEach((r, index) => {
    if (r.isStaff) {
      return;
    }

    r.order = hasStaff ? index : index + 1;
  });
}

export function ReviewersDatagrid({
  reviewers,
  setReviewers,
  reviewMode,
  setValue,
  handleAddReviewer,
  disabled = false,
  isStaff = false,
}: ReviewersDatagridProps) {
  const reviewerIssues = useReviewerIssues();
  const reviewersIssues = useReviewersIssues();
  const { hasPermissions } = useAuthorization();
  const { tenantId } = useTenant();

  const canManageUsers = hasPermissions([Permission.WriteAllUserTenant]);

  // Filter individual reviewer issues by checking if reviewer IDs appear in schemaStack
  const individualReviewerIssues = useMemo(
    () =>
      reviewerIssues.filter((i) =>
        reviewers.some((r) => i.schemaStack?.some((schemaStack) => schemaStack.name === r.id))
      ),
    [reviewerIssues, reviewers]
  );

  // Combine collection-level issues with individual reviewer issues for summary display
  const allReviewerIssues = useMemo(
    () => [...reviewersIssues, ...individualReviewerIssues],
    [reviewersIssues, individualReviewerIssues]
  );

  const [confirmAction, setConfirmAction] = useState<{
    type: "reset" | "complete";
    reviewerId: string;
    reviewerName: string;
  } | null>(null);

  const handleConfirmAction = useCallback(
    (type: "reset" | "complete", reviewerId: string, reviewerName: string) => {
      setConfirmAction({ type, reviewerId, reviewerName });
    },
    []
  );

  const handleConfirm = useCallback(() => {
    if (!confirmAction || !setReviewers) return;
    const updatedReviewers = reviewers.map((r) => {
      if (r.submissionReviewerId !== confirmAction.reviewerId) return r;
      if (confirmAction.type === "reset") {
        return { ...r, reviewerStatus: null };
      }
      return { ...r, reviewerStatus: ReviewerStatus.Approved };
    });
    setReviewers(updatedReviewers);
    setConfirmAction(null);
  }, [confirmAction, reviewers, setReviewers]);

  const augmentedReviewersRows = useMemo(
    () =>
      reviewers.map((r) => ({
        ...r,
        __reorder__: r.name,
      })),
    [reviewers]
  );
  const columns = useMemo(() => {
    const cols: GridColDef<Reviewer>[] = [];

    const issuesColumn: GridColDef<Reviewer> = {
      field: "issues",
      display: "flex",
      flex: 0.6,
      headerName: "Issues",
      renderCell: ({ row }) => {
        const issues = reviewerIssues.filter((i) => {
          return i.schemaStack?.some((s) => s.name === row.id);
        });
        return <IssueCount issueCount={issues.length} />;
      },
    };

    if (isStaff && !disabled) {
      cols.push(
        {
          field: "approval",
          display: "flex",
          width: 48,
          headerName: "",
          sortable: false,
          renderCell: ({ row }) => {
            if (row.reviewerStatus === ReviewerStatus.Approved) {
              return <CheckCircle color="success" fontSize="small" />;
            }
            if (row.reviewerStatus === ReviewerStatus.Rejected) {
              return <Cancel color="error" fontSize="small" />;
            }
            if (row.submissionReviewerId) {
              return (
                <Tooltip title="Mark as approved">
                  <Box
                    onClick={(event) => {
                      event.stopPropagation();
                      handleConfirmAction("complete", row.submissionReviewerId!, row.name);
                    }}
                    sx={{ cursor: "pointer", display: "flex", "&:hover": { opacity: 0.7 } }}
                  >
                    <RadioButtonUnchecked color="disabled" fontSize="small" />
                  </Box>
                </Tooltip>
              );
            }
            return <RadioButtonUnchecked color="disabled" fontSize="small" />;
          },
        },
        {
          field: "reviewerStatus",
          display: "flex",
          flex: 0.5,
          headerName: "Status",
          sortable: false,
          renderCell: ({ row }) => {
            if (row.reviewerStatus === ReviewerStatus.Approved) {
              return (
                <Typography variant="body2" fontWeight={700}>
                  Approved
                </Typography>
              );
            }
            if (row.reviewerStatus === ReviewerStatus.Rejected) {
              return (
                <Typography variant="body2" fontWeight={700}>
                  Rejected
                </Typography>
              );
            }
            return <Typography variant="body2">Pending</Typography>;
          },
        }
      );
    }

    if (reviewMode === ReviewMode.Sequential) {
      cols.push({
        field: "order",
        flex: 0.3,
        headerName: "Order",
        align: "center",
        headerAlign: "center",
        valueGetter: (_, row) => (row.order !== 0 ? row.order : ""),
      });
    }

    cols.push(
      {
        field: "name",
        display: "flex",
        flex: 1,
        sortable: false,
        headerName: "Reviewer Name",
      },
      {
        field: "email",
        display: "flex",
        flex: 1,
        sortable: false,
        headerName: "Email",
      },
      {
        field: "dueDate",
        display: "flex",
        flex: 1,
        headerName: "Due date",
        sortable: false,
        valueGetter: (_, row) => (row.dueDate ? prettyPrintDate(row.dueDate.toISO()) : ""),
      }
    );

    if (reviewerIssues.length > 0) {
      cols.push(issuesColumn);
    }

    if (!disabled) {
      cols.push({
        field: "actions",
        type: "actions",
        width: 80,
        align: "right",
        headerName: "",
        sortable: false,
        getActions: ({ row }) => {
          const hasProgress =
            row.reviewerStatus === ReviewerStatus.Approved ||
            row.reviewerStatus === ReviewerStatus.Rejected;

          const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

          if (isStaff && row.submissionReviewerId && hasProgress) {
            actions.push(
              <GridActionsCellItem
                key="reset"
                label="Reset Progress"
                icon={
                  <Tooltip title="Reset progress">
                    <RestartAlt color="action" />
                  </Tooltip>
                }
                onClick={() => {
                  handleConfirmAction("reset", row.submissionReviewerId!, row.name);
                }}
              />
            );
          }

          if (!disabled) {
            actions.push(
              <GridActionsCellItem
                key="delete"
                label="Delete"
                icon={<RemoveFilled />}
                onClick={() => {
                  setReviewers?.(reviewers.filter((r) => r.id !== row.id));
                  setReviewerOrder(reviewers.filter((r) => r.id !== row.id));
                }}
              />
            );
          }

          return actions;
        },
      });
    }

    return cols;
  }, [reviewMode, reviewers, setReviewers, reviewerIssues, disabled, isStaff, handleConfirmAction]);

  const pinnedRows = useMemo(
    () => ({
      top: augmentedReviewersRows.filter((r) => r.isStaff), // Pin staff reviewers at the top
    }),
    [augmentedReviewersRows]
  );

  return (
    <Box sx={{ mt: 1 }}>
      <PriorityOrderSwitch
        value={reviewMode}
        onChange={(newReviewMode: ReviewMode) =>
          setValue?.("reviewMode", newReviewMode, {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
        disabled={disabled}
      />
      {allReviewerIssues.length > 0 && (
        <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
          {allReviewerIssues.map((issue, index) => (
            <Typography key={index} variant="body2">
              {renderIssueMessage(issue.message, canManageUsers, tenantId)}
            </Typography>
          ))}
        </Alert>
      )}
      {disabled ? (
        <SubmissionFormDatagrid
          rows={augmentedReviewersRows}
          columns={columns}
          rowReordering={false}
          pinnedRows={pinnedRows}
        />
      ) : (
        <DataGridWithAddButton
          rows={augmentedReviewersRows}
          columns={columns}
          onAdd={handleAddReviewer}
          createEmptyRow={() => ({
            id: "empty",
            name: "",
            email: "",
            dueDate: undefined,
            streetAddress1: undefined,
            streetAddress2: undefined,
            city: undefined,
            state: undefined,
            zip: undefined,
            country: undefined,
            isSigner: false,
            isStaff: false,
            order: 0,
          })}
          rowReordering={reviewMode === ReviewMode.Sequential}
          pinnedRows={pinnedRows}
          onRowOrderChange={(params) => {
            const { oldIndex, targetIndex } = params;
            const hasStaff = reviewers.some((r) => r.isStaff);
            const o = hasStaff ? oldIndex + 1 : oldIndex;
            const n = hasStaff ? targetIndex + 1 : targetIndex;

            if (setReviewers) {
              const newList = [...reviewers];
              const [movedItem] = newList.splice(o, 1);
              newList.splice(n, 0, movedItem);
              setReviewerOrder(newList);
              setReviewers(newList);

              // Transform reviewers from UI format to form format and trigger validation
              const formReviewers = newList.map((r) => ({
                userId: r.id,
                reviewOrder: r.order,
                isRequired: true,
                isSigner: r.isSigner ?? false,
                isStaff: r.isStaff,
                dueDate: r.dueDate,
              }));

              setValue?.("reviewers", formReviewers, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }
          }}
          addButtonConfig={{
            buttonText: "Add Reviewer",
            icon: <PersonAdd />,
          }}
          gridProps={{
            onRowClick: handleAddReviewer,
          }}
        />
      )}
      <ConfirmDialog
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        header={
          confirmAction?.type === "reset" ? "Reset Reviewer Progress" : "Mark Reviewer Complete"
        }
        info={
          confirmAction?.type === "reset"
            ? `This will clear all progress for ${confirmAction?.reviewerName ?? "this reviewer"}, including approval status and document views.`
            : `This will mark ${confirmAction?.reviewerName ?? "this reviewer"} as approved and all their documents as viewed. This is a staff override.`
        }
        confirmText={confirmAction?.type === "reset" ? "Yes, Reset" : "Yes, Complete"}
      />
    </Box>
  );
}
